import type { CategoryWithTranslations, Product } from '@agos/api-client/model'
import { T, tt } from '@agos/shared'
import { StatusBadge, cn } from '@agos/ui'
import {
  Box,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ImageOff,
  Info,
  Plus,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'

import { CategoryPicker, SlotImageUpload, SlotPresetEditor } from './SlotEditors'
import { fixedPriceOf, fixedPriceText } from './types'
import type { BurgerBoxItem, BurgerBoxSlot } from './types'

const LABEL_CLASSES = 'text-xs font-bold text-muted-foreground uppercase tracking-wider'
const FIELD_CLASSES =
  'w-full px-3 py-2.5 rounded-xl bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

export function BoxEditor({
  item,
  categories,
  products,
  onChange,
  onDelete,
}: {
  item: BurgerBoxItem
  categories: CategoryWithTranslations[]
  products: Product[]
  onChange: (patch: Partial<BurgerBoxItem>) => void
  onDelete: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(true)

  const updateSlot = (slotId: string, patch: Partial<BurgerBoxSlot>) =>
    onChange({
      slots: item.slots.map((slot) => (slot.id === slotId ? { ...slot, ...patch } : slot)),
    })

  const addSlot = () =>
    onChange({
      slots: [
        ...item.slots,
        {
          id: `slot_${Date.now()}`,
          label: tt('Nový slot'),
          categoryId: null,
          required: false,
          presetItems: [],
        },
      ],
    })

  const removeSlot = (slotId: string) =>
    onChange({ slots: item.slots.filter((slot) => slot.id !== slotId) })

  const moveSlot = (slotId: string, direction: -1 | 1) => {
    const slots = [...item.slots]
    const from = slots.findIndex((slot) => slot.id === slotId)
    const to = from + direction
    if (from === -1 || to < 0 || to >= slots.length) return
    const moved = slots[from]
    const displaced = slots[to]
    if (!moved || !displaced) return
    slots[from] = displaced
    slots[to] = moved
    onChange({ slots })
  }

  const triggerCategoryName = categories.find(
    (category) => category.id === item.triggerCategoryId,
  )?.name

  return (
    <div
      className={cn(
        'bg-card border rounded-2xl overflow-hidden transition-all',
        item.active ? 'border-primary/30' : 'border-border',
      )}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none hover:bg-muted/30 transition-colors"
        onClick={() => setIsExpanded((expanded) => !expanded)}
      >
        <div
          className={cn(
            'w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0',
            item.active ? 'bg-amber-500/15' : 'bg-muted',
          )}
        >
          <Box className={cn('w-4 h-4', item.active ? 'text-amber-500' : 'text-muted-foreground')} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-foreground text-sm truncate">
            {item.name || tt('Box bez názvu')}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {triggerCategoryName
              ? `${tt('Kategória')}: ${triggerCategoryName}`
              : tt('Kategória nenastavená')}{' '}
            · {item.slots.length} {tt('slotov')}
          </p>
        </div>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onChange({ active: !item.active })
          }}
          className="flex-shrink-0"
        >
          <StatusBadge
            label={item.active ? tt('Aktívny') : tt('Neaktívny')}
            color={item.active ? 'green' : 'zinc'}
            icon={item.active ? ToggleRight : ToggleLeft}
            size="md"
          />
        </button>
        <ChevronRight
          className={cn(
            'w-4 h-4 text-muted-foreground transition-transform flex-shrink-0',
            isExpanded && 'rotate-90',
          )}
        />
      </div>

      {isExpanded && (
        <div className="border-t border-border px-4 py-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={LABEL_CLASSES} htmlFor="box-nazov-boxu">
                <T>Názov boxu</T>
              </label>
              <input
                id="box-nazov-boxu"
                type="text"
                value={item.name}
                onChange={(event) => onChange({ name: event.target.value })}
                className={FIELD_CLASSES}
                placeholder={tt('napr. Kombo BOX')}
              />
            </div>
            <div className="space-y-1">
              <label className={LABEL_CLASSES} htmlFor="box-popis-pre-zakaznika">
                <T>Popis (pre zákazníka)</T>
              </label>
              <input
                id="box-popis-pre-zakaznika"
                type="text"
                value={item.description}
                onChange={(event) => onChange({ description: event.target.value })}
                className={FIELD_CLASSES}
                placeholder={tt('Zostavte si vlastný box...')}
              />
            </div>
          </div>

          {/* §10A.107, owner item E - *"ak dám že stojí 7.50 tak bude stáť toľko"*. */}
          <div className="space-y-1">
            <label className={LABEL_CLASSES} htmlFor="box-pevna-cena">
              <T>Pevná cena boxu</T>
            </label>
            <p className="text-xs text-muted-foreground -mt-1">
              <T>
                Box sa predá za túto cenu bez ohľadu na ceny produktov v ňom. Prázdne pole znamená,
                že sa spočítajú ceny jednotlivých častí.
              </T>
            </p>
            <div className="flex items-center gap-2">
              <input
                id="box-pevna-cena"
                type="text"
                inputMode="decimal"
                data-box-fixed-price
                value={fixedPriceText(item.fixedPrice)}
                // Stored as typed so *7,* is not fought with mid-entry; parsed on save, where
                // `fixedPriceOf` accepts the comma a Slovak keyboard produces.
                onChange={(event) => onChange({ fixedPrice: fixedPriceOf(event.target.value) })}
                className={cn(FIELD_CLASSES, 'max-w-[10rem]')}
                placeholder={tt('napr. 7,50')}
              />
              <span className="text-sm font-semibold text-muted-foreground">€</span>
            </div>
          </div>

          <label
            className={cn(
              'flex items-center gap-3 cursor-pointer p-3 border rounded-xl transition-colors',
              item.hideProductImages
                ? 'border-orange-500/30 bg-orange-500/8'
                : 'border-border bg-muted/20 hover:bg-muted/40',
            )}
          >
            <input
              type="checkbox"
              checked={Boolean(item.hideProductImages)}
              onChange={(event) => onChange({ hideProductImages: event.target.checked })}
              className="w-4 h-4 rounded accent-orange-500 flex-shrink-0"
            />
            <div>
              <span className="text-sm font-bold flex items-center gap-1.5">
                <ImageOff className="w-4 h-4 text-orange-500" />{' '}
                <T>Skryť obrázky produktov v slotoch</T>
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                <T>Zákazník uvidí len názov a cenu - obrázky produktov sa neobrazia</T>
              </p>
            </div>
          </label>

          <div className="space-y-2">
            <label className={LABEL_CLASSES}>
              <T>Kategória spustenia</T>
            </label>
            <p className="text-xs text-muted-foreground -mt-1">
              <T>V ktorej kategórii sa má tento box zobraziť zákazníkovi?</T>
            </p>
            <CategoryPicker
              value={item.triggerCategoryId}
              onChange={(categoryId) => onChange({ triggerCategoryId: categoryId })}
              categories={categories}
              placeholder={tt('- vyberte kategóriu (napr. Burgre) -')}
            />
            {item.triggerCategoryId && (
              <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2 text-xs text-amber-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  <T>Zobrazí sa, keď zákazník otvorí kategóriu</T> „{triggerCategoryName}".
                </span>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className={LABEL_CLASSES}>
                  <T>Zloženie boxu - sloty</T>
                </label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <T>Pre každý slot nastavte kategóriu a voliteľne konkrétne produkty.</T>
                </p>
              </div>
              <button
                type="button"
                onClick={addSlot}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary/10 text-primary border border-primary/20 text-xs font-bold hover:bg-primary/20 transition-colors whitespace-nowrap flex-shrink-0"
              >
                <Plus className="w-3.5 h-3.5" /> <T>Pridať slot</T>
              </button>
            </div>

            {item.slots.length === 0 && (
              <div className="text-center py-6 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
                <T>Žiadne sloty. Kliknite na „Pridať slot".</T>
              </div>
            )}

            <div className="space-y-2">
              {item.slots.map((slot, index) => (
                <div
                  key={slot.id}
                  className="border border-border rounded-xl p-3 space-y-2.5 bg-background/50"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        aria-label={tt('Posunúť vyššie')}
                        onClick={() => moveSlot(slot.id, -1)}
                        disabled={index === 0}
                        className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-3 h-3 text-muted-foreground" />
                      </button>
                      <button
                        type="button"
                        aria-label={tt('Posunúť nižšie')}
                        onClick={() => moveSlot(slot.id, 1)}
                        disabled={index === item.slots.length - 1}
                        className="p-0.5 rounded hover:bg-muted transition-colors disabled:opacity-25 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>

                    <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[10px] font-black flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>

                    <input
                      type="text"
                      value={slot.label}
                      onChange={(event) => updateSlot(slot.id, { label: event.target.value })}
                      className="flex-1 min-w-0 px-3 py-2 rounded-xl bg-background border border-border text-sm font-semibold outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder={tt('Názov slotu (napr. Burger)')}
                    />

                    <button
                      type="button"
                      onClick={() => updateSlot(slot.id, { required: !slot.required })}
                      className={cn(
                        'px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all whitespace-nowrap flex-shrink-0',
                        slot.required
                          ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                          : 'bg-muted text-muted-foreground border-transparent hover:border-border',
                      )}
                    >
                      {slot.required ? tt('Povinný') : tt('Voľný')}
                    </button>

                    <button
                      type="button"
                      aria-label={tt('Odstrániť slot')}
                      onClick={() => removeSlot(slot.id)}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-all flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <div className="pl-7">
                    {/* Changing the category clears the presets: they belonged
                        to the old one. */}
                    <CategoryPicker
                      value={slot.categoryId}
                      onChange={(categoryId) =>
                        updateSlot(slot.id, { categoryId, presetItems: [] })
                      }
                      categories={categories}
                      placeholder={tt('- vyberte kategóriu produktov -')}
                    />
                    {slot.categoryId && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <T>Zákazník si vyberie z:</T>{' '}
                        <strong className="text-foreground">
                          {categories.find((category) => category.id === slot.categoryId)?.name ??
                            '-'}
                        </strong>
                        {slot.required ? tt(' (povinné)') : tt(' (voliteľné)')}
                      </p>
                    )}
                  </div>

                  <SlotImageUpload
                    imageUrl={slot.imageUrl}
                    onUpdate={(url) => updateSlot(slot.id, { imageUrl: url })}
                  />

                  <SlotPresetEditor
                    slot={slot}
                    products={products}
                    onUpdate={(patch) => updateSlot(slot.id, patch)}
                  />
                </div>
              ))}
            </div>

            {item.slots.length > 0 && (
              <button
                type="button"
                onClick={addSlot}
                className="w-full py-2 rounded-xl border border-dashed border-primary/30 text-primary/70 text-sm font-semibold hover:border-primary/60 hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" /> <T>Pridať ďalší slot</T>
              </button>
            )}
          </div>

          <div className="flex justify-end pt-1 border-t border-border/50">
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" /> <T>Odstrániť tento box</T>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
